/**
 * The rules a conversation follows, in the one place both sides can read them.
 *
 * The interesting problem in a chat that works offline is not the messages —
 * they are append-only rows and the sync engine already carries those. It is
 * the *channel*: two people can open a conversation with each other while both
 * are on a train, and when the tunnel ends there must be one conversation, not
 * two containing half the history each.
 *
 * So a direct conversation has no invented id. Its id is derived from who is in
 * it, which makes "create it" and "find it" the same operation and makes the
 * question of who created it first stop mattering.
 */

export type ChannelKind = 'channel' | 'direct';

/** What a person wants to hear about, per conversation. */
export type ChannelNotify = 'all' | 'mentions' | 'none';

/**
 * Who may change a channel's membership.
 *
 * Set per channel rather than per instance, because a team channel and a
 * channel a client can see want different answers and the same workspace holds
 * both. `members` is the default — a channel nobody can be invited to is a
 * channel that dies with its creator — and `admins` is the one to reach for
 * when who is in the room is itself the sensitive part.
 */
export type InvitePolicy = 'members' | 'admins';

export const isInvitePolicy = (value: unknown): value is InvitePolicy =>
  value === 'members' || value === 'admins';

/**
 * Whether this person may add or remove people here.
 *
 * Being in the channel is required either way: `admins` widens who counts, it
 * does not let somebody manage a conversation they are not part of. A direct
 * conversation has no membership to manage at all — its members are its id.
 */
export function canManageMembers(
  channel: ChannelLike & { invite_policy?: string | null; created_by?: string | null },
  userId: string,
  isWorkspaceAdmin: boolean,
): boolean {
  if (channel.kind === 'direct') return false;
  if (!isMember(channel, userId)) return false;
  if ((channel.invite_policy ?? 'members') === 'members') return true;
  return channel.created_by === userId || isWorkspaceAdmin;
}

/**
 * The id of the conversation between exactly these two people.
 *
 * Sorted, so it does not matter who opened it; prefixed, so it is obvious in a
 * database dump what kind of row it is; and built from the ids themselves
 * rather than a hash of them, because a hash can collide and a concatenation
 * of two unique strings cannot. Two people is the whole of it — see
 * `isDirectId` and the note on group conversations below.
 */
export function directChannelId(a: string, b: string): string {
  if (a === b) throw new Error('a direct conversation needs two different people');
  return `dm.${[a, b].sort().join('.')}`;
}

export const isDirectId = (id: string): boolean => /^dm\.[^.]+\.[^.]+$/.test(id);

/** The two people a direct id names, or null if it does not name two. */
export function directMembers(id: string): [string, string] | null {
  const parts = id.split('.');
  if (parts.length !== 3 || parts[0] !== 'dm') return null;
  return [parts[1], parts[2]];
}

/**
 * A conversation with three or more people is a **private channel with a
 * name**, not a bigger direct message. That is a product decision and a
 * technical one at once: a derived id only stays collision-free while it is a
 * concatenation, and a group people can name is a group people can find again.
 */
export const MAX_DIRECT_MEMBERS = 2;

export interface ChannelLike {
  id: string;
  kind: string;
  name?: string | null;
  is_private?: number | boolean;
  members?: string[] | null;
  archived_at?: number | null;
  deleted_at?: number | null;
}

export interface MessageLike {
  id: string;
  channel_id: string;
  author_id?: string | null;
  body?: string | null;
  created_at: number;
  deleted_at?: number | null;
}

/**
 * Whether somebody is in a conversation.
 *
 * An open channel has an empty member list, which means the workspace rather
 * than nobody — the alternative is writing every member into every channel and
 * keeping that list correct as people join and leave.
 */
export function isMember(channel: ChannelLike, userId: string): boolean {
  if (!channel.is_private) return true;
  return (channel.members ?? []).includes(userId);
}

/**
 * The order a conversation reads in.
 *
 * Time alone is not enough: `created_at` is stamped by the server as it
 * applies a batch, so an outbox flushed after a tunnel lands in one
 * millisecond and the stamp says nothing about which line came first. Within
 * a tie the truth is `seq` — the order the server actually applied them,
 * which for one device's batch is the order they were typed. A row still on
 * its way has no seq yet and sorts after the acked ones, which is where the
 * newest line belongs anyway; the id settles what is left, by plain
 * comparison rather than `localeCompare`, which may answer differently per
 * locale.
 */
export function messageOrder(
  a: { created_at: number; id: string; seq?: number | null },
  b: { created_at: number; id: string; seq?: number | null },
): number {
  return a.created_at - b.created_at
    || (a.seq ?? Number.MAX_SAFE_INTEGER) - (b.seq ?? Number.MAX_SAFE_INTEGER)
    || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

/** Everything unread here, not counting your own. */
export function unreadCount(messages: MessageLike[], lastReadAt: number, userId: string): number {
  let count = 0;
  for (const message of messages) {
    if (message.deleted_at) continue;
    if (message.author_id === userId) continue;
    if (message.created_at > lastReadAt) count += 1;
  }
  return count;
}

/**
 * What to call a conversation on screen.
 *
 * A direct conversation has no name of its own — it is "the other person", and
 * which person that is depends on who is looking. `nameOf` resolves a user id;
 * an id it does not know stays an id rather than becoming a blank, because a
 * blank row in a list is unclickable in a way an ugly one is not.
 */
export function channelTitle(
  channel: ChannelLike,
  viewerId: string,
  nameOf: (userId: string) => string | undefined,
): string {
  if (channel.kind !== 'direct') return channel.name ?? '';
  const others = (channel.members ?? []).filter((id) => id !== viewerId);
  if (!others.length) return nameOf(viewerId) ?? '';
  return others.map((id) => nameOf(id) ?? id).join(', ');
}

/** Deterministic, so two devices marking the same channel read converge. */
export const readStateId = (channelId: string, userId: string): string => `${channelId}::${userId}`;

/**
 * Channel names are lowercase and dash-joined, the way every tool that has one
 * does it — not for tradition but because `#design review` and `#design-review`
 * are two channels that look like one.
 */
export function normaliseChannelName(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

/* ------------------------------------------------------------- being named */

/** The little a mention needs to know about somebody. */
export interface Mentionable {
  id: string;
  name?: string | null;
  email?: string | null;
}

/**
 * Who this text names.
 *
 * Shared because both sides need the same answer to the same question, and
 * they used to reach it separately: the server decided who to notify, and the
 * screen decided whether a channel on "only when I am named" should count
 * towards the badge. Two implementations of "is this me" is how a badge comes
 * to disagree with the notification it is supposed to be counting.
 *
 * A person answers to their email, the part of it before the `@`, their name
 * without spaces, and their first name — the last of those being the handle
 * the composer inserts, so what it writes is what this reads back. One
 * character is not a handle; it would make `@a` in a sentence a mention.
 */
export function findMentions(people: Mentionable[], text: string): string[] {
  const body = String(text ?? '');
  if (!body.includes('@')) return [];

  const handles = new Map<string, string>();
  for (const person of people) {
    const email = String(person.email ?? '').toLowerCase();
    const name = String(person.name ?? '').toLowerCase();
    for (const handle of [email, email.split('@')[0], name.replace(/\s+/g, ''), name.split(/\s+/)[0]]) {
      if (handle && handle.length > 1 && !handles.has(handle)) handles.set(handle, person.id);
    }
  }

  const found = new Set<string>();
  for (const match of body.matchAll(/@([\w.+-]+(?:@[\w.-]+\.[a-z]{2,})?)/gi)) {
    const userId = handles.get(match[1].toLowerCase());
    if (userId) found.add(userId);
  }
  return [...found];
}

/** Whether this text names this person. */
export const namesMe = (people: Mentionable[], text: string, me: string): boolean =>
  findMentions(people, text).includes(me);

/* --------------------------------------------------- what counts as urgent */

/**
 * Notification kinds somebody on "only what needs me" still wants delivered.
 *
 * One definition rather than one per channel, because a rule that differs by
 * channel is a rule nobody can predict — that sentence was in the code while
 * two sets quietly disagreed, which is how this came to be written down.
 */
export const IMPORTANT_KINDS = ['assigned', 'mention', 'invite', 'due_soon', 'agenda'] as const;

/**
 * ...and the one kind the *instant* channels add.
 *
 * A chat message is urgent or it is nothing: it belongs on a phone in a second
 * or not at all. Email here is batched into a digest on purpose, and a chat
 * message that arrives in a two-hour summary is a message answered too late to
 * matter — so it is important for Telegram and Web Push and deliberately not
 * for email. That is a real difference, and it is stated instead of hidden.
 */
export const INSTANT_ONLY_KINDS = ['message'] as const;

export const isImportantFor = (channel: 'email' | 'instant', kind: string): boolean =>
  (IMPORTANT_KINDS as readonly string[]).includes(kind)
  || (channel === 'instant' && (INSTANT_ONLY_KINDS as readonly string[]).includes(kind));
